import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import SignaturePadLib from 'signature_pad';

export type SignaturePadHandle = {
  isEmpty(): boolean;
  clear(): void;
  toDataUrl(): string;
};

/**
 * Wrapper de `signature_pad` con canvas HiDPI.
 * Ajusta width/height del canvas al tamaño real del elemento * devicePixelRatio
 * para que la firma no se vea pixelada en mobile.
 */
export const SignaturePad = forwardRef<SignaturePadHandle>((_, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePadLib | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const pad = new SignaturePadLib(canvas, { backgroundColor: 'rgb(255,255,255)' });
    padRef.current = pad;

    function resize(): void {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d')?.scale(ratio, ratio);
      pad.clear();
    }
    resize();
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      pad.off();
    };
  }, []);

  useImperativeHandle(ref, () => ({
    isEmpty: () => padRef.current?.isEmpty() ?? true,
    clear: () => padRef.current?.clear(),
    toDataUrl: () => padRef.current!.toDataURL('image/png'),
  }));

  return <canvas ref={canvasRef} className="sig-pad" />;
});
SignaturePad.displayName = 'SignaturePad';
